// When a P&L category is renamed, every project's view overlay moves with it.
//
// Run: deno test --allow-read src/features/pnlAnalytics/projectPnlView.test.ts
// Run: npm run verify:pnl-project-view
//
// WHY THIS EXISTS. The overlay in claims_project_config.config -> 'pnl_view'
// keys on the category NAME, because that is what the organisation list, the
// value rows and the tab's row identity all use. So a rename at organisation
// level leaves every project's entry naming a category that no longer exists:
// the resolver ignores it, by design, and the line comes back on every project
// unhidden, in organisation order, under the organisation's new word instead of
// the project's own. Nobody typed that, and nobody is told it happened.
//
// This is the third thing an organisation rename carries, beside the analytics
// alias (aliasWrite.ts) and the typed values (categoryRenameValues.ts), and it
// runs in the same editor save, on the same renames detectCategoryRenames has
// vouched for, which returns nothing unless the list length is unchanged and
// every other field on the entry is identical.
//
// WHAT IT DELIBERATELY DOES NOT DO. It never adds a pnl_view block to a project
// that has none. A project with no overlay has nothing to carry, and writing an
// empty block to all 28 projects would put 28 rows into
// claims_project_config_history to record that nothing changed.
//
// The client is described structurally rather than imported as SupabaseClient,
// for the same reason categoryRenameValues.ts does it: Deno cannot resolve
// @supabase/supabase-js's types without a populated node_modules, so importing
// them would make this module untestable by the only test runner this repo has.
// It is also what lets the test drive the real function against a recording
// fake rather than asserting on a message and hoping the call underneath
// matches it.

import type { CategoryRename } from "./renameDetection.ts";
import { parseProjectPnlView, serialiseProjectPnlView, withRenamedCategory } from "./projectPnlView.ts";

export interface OverlayConfigRow {
  project_id: string;
  config: Record<string, unknown> | null;
}

export interface OverlayReadResult {
  data: OverlayConfigRow[] | null;
  error: { message: string } | null;
}

export interface OverlayWriteResult {
  data: { project_id: string }[] | null;
  error: { message: string } | null;
}

export interface OverlayReadFilter {
  eq(column: string, value: string): PromiseLike<OverlayReadResult>;
}

export interface OverlayWriteSelect {
  select(columns: string): PromiseLike<OverlayWriteResult>;
}

export interface OverlayWriteFilter {
  eq(column: string, value: string): OverlayWriteSelect;
}

export interface OverlayConfigTable {
  select(columns: string): OverlayReadFilter;
  update(values: { config: Record<string, unknown> }): OverlayWriteFilter;
}

export interface OverlayConfigClient {
  from(table: string): OverlayConfigTable;
}

export interface CarryRenamedCategoryOverlaysResult {
  /** Null on full success. Non-null names the first failure; earlier projects in the batch may already have been carried. */
  error: string | null;
  /** Projects whose overlay actually changed and was written. */
  changedProjectCount: number;
}

/**
 * Rewrites the old-to-new pair inside every project's pnl_view block, for this
 * organisation only.
 *
 * The returned rows are counted rather than assumed: an update refused by row
 * level security returns neither an error nor any rows, which is the silent
 * no-op this codebase has been bitten by. A project whose overlay does not
 * mention the renamed category is never written to at all.
 */
export async function carryRenamedCategoryOverlays(
  client: OverlayConfigClient,
  organizationId: string,
  renames: CategoryRename[],
): Promise<CarryRenamedCategoryOverlaysResult> {
  if (renames.length === 0) return { error: null, changedProjectCount: 0 };

  const { data: rows, error: readError } = await client
    .from("claims_project_config")
    .select("project_id, config")
    .eq("organization_id", organizationId);

  if (readError) {
    return {
      error: `Could not read the project P&L layouts: ${readError.message}`,
      changedProjectCount: 0,
    };
  }

  let changedProjectCount = 0;

  for (const row of rows ?? []) {
    const config = row.config ?? {};
    const before = parseProjectPnlView(config);
    let after = before;
    for (const { oldName, newName } of renames) {
      after = withRenamedCategory(after, oldName, newName);
    }

    // Nothing moved, nothing written. This is also what keeps a project that
    // has no overlay from being given one: an absent block parses to inherit
    // everything, a rename leaves it inherit everything, and the comparison
    // skips it. A guard on the block's presence as well would be a second rule
    // that no test could ever tell apart from this one.
    const next = serialiseProjectPnlView(after);
    if (JSON.stringify(serialiseProjectPnlView(before)) === JSON.stringify(next)) continue;

    const { data, error } = await client
      .from("claims_project_config")
      .update({ config: { ...config, pnl_view: next } })
      .eq("project_id", row.project_id)
      .select("project_id");

    if (error) {
      return {
        error: `Could not move one project's P&L layout to the new category name: ${error.message}`,
        changedProjectCount,
      };
    }
    if (!data || data.length === 0) {
      return {
        error: "One project's P&L layout was not saved, so you may not have permission to change it.",
        changedProjectCount,
      };
    }

    changedProjectCount += 1;
  }

  return { error: null, changedProjectCount };
}

/**
 * What to tell the user when the overlay carry fails partway.
 *
 * Says the one thing that matters: the rename itself went through, and on any
 * project this did not reach, the renamed line is back on the P&L under the new
 * name, which is visible and fixable rather than silent.
 */
export function overlayCarryFailureMessage(error: string): string {
  return `${error} The category list was saved, so on any project that hid, reordered or relabelled this category, the line is now back on its P&L under the new name. Set it again on that project's P&L tab.`;
}
